import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { format } from 'date-fns';
import {
  browserName,
  browserVersion,
  osName,
  osVersion,
} from 'react-device-detect';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { nanoid } from 'nanoid';
import useWebSocket, { Options } from 'react-use-websocket';
import useAsyncEffect from 'use-async-effect';
import { useSnackbar } from 'notistack';
import { Hub } from 'aws-amplify/utils';

import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { useHandleApiErrors, useAuthTokens } from './hooks';
import { useHandleWebsocketError } from '@hooks/websocket';
import { useAuth } from '@core/auth';

import { getAppTheme, getAppSettings } from './helpers';
import {
  setSentryApiVersion,
  setSentryAuthUserData,
  setSentryFrontendVersion,
} from '@helpers/sentry';
import { removeLocalStorageValue } from '@helpers/storage';
import UserService from '@services/UserService';
import ROUTES from '@constants/routes';

import { IUser, ILogoutForced, LogoutReasonEnum } from '@interfaces/user';
import {
  IWebSocketConnectionInfo,
  WebsocketAction,
} from '@interfaces/webSocketConnectionInfo';
import { InitialValues } from '@components/UserProfileModal/UserProfileForm/interfaces';
import { CurrentScreenEnum, GetWebSocketOptionsType } from './interfaces';
import { StorageValues } from '@interfaces/storage';
import { reloadPageTimeout } from '@helpers';

import { default as mytaverseWebsocketLogger } from './logger';

import ParticipantsService from '../../services/ParticipantsService';

import { getNotification } from '@components/Notification/helpers';
import { useNotificationContext } from '@providers/NotificationProvider';
import { useNavigationHistory } from '@providers/NavigationHistoryProvider';
import { IConfigs } from '@interfaces/profile';
import { LANGUAGE_NAMESPACE } from '@constants/language';

import AnalyticsService from '@services/AnalyticsService';
import { AnalyticsType } from '@interfaces/analytics';
import { IUserAvatar } from '@interfaces/avatar';
import { env } from 'env';

const { REACT_APP_HUB_VERSION, REACT_APP_API_URL } = env;

export { mytaverseWebsocketLogger };

import { useAssetPreloader } from '@components/AssetPreloader';
import { AvaturnStatuses, INewAvaturnData } from '@features/avaturn/interfaces';
import { MytaverseLogger } from '@helpers/logger';
import { IWorldSettings } from '@interfaces/worlds';
import { IMytaverseContext } from './interfaces';
import ParticipantScopes from '@constants/scopes';
import { getDeviceProfile } from '@core/dashboard/providers/MytaverseEventProvider/helpers';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const MytaverseContext = React.createContext<IMytaverseContext>({});

export const useMytaverse = () => useContext(MytaverseContext);

type MytaverseProviderProps = {
  children: ReactNode;
};

export const MytaverseProvider: React.FC<MytaverseProviderProps> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();
  const { t: translate, i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    localStorage.getItem('language') || 'EN',
  );

  const { enqueueSnackbar } = useSnackbar();
  const { setNotificationTheme } = useNotificationContext();

  const token = useRef<string>('');
  const [user, setUser] = useState<IUser | any>();
  const [userPermissions, setUserPermissions] = useState<ParticipantScopes[]>(
    [],
  );
  const [userId, setUserId] = useState<string | null>(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  const trackLoginEvent = useRef(false);
  const logoutClicked = useRef(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isLogoutForced, setIsLogoutForced] = useState<ILogoutForced | false>(
    false,
  );

  const [isDebugMode, setIsDebugMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [changingUserData, setChangingUserData] = useState(false);
  const websocketSessionIdRef = useRef(nanoid(20));
  const [pageClicked, setPageClicked] = useState(false);

  const { signIn: signInWithLanguage, signOut, getAuthTokens, authUser } = useAuth();

  const signIn = useCallback(() => {
    return signInWithLanguage(selectedLanguage.toLowerCase());
  }, [signInWithLanguage, selectedLanguage]);
  const { fetchAndSetAuthTokens, refreshAuthToken } = useAuthTokens({
    token,
    // idToken,
    getAuthTokens,
  });

  const { showNotification, hideNotification } = useNotification();

  const [appTheme, setAppTheme] = useState<any | null>(null);
  useAssetPreloader({ appTheme, isAuthenticated });
  const [appSettings, setAppSettings] = useState<IWorldSettings | null>(null);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { history } = useNavigationHistory();
  const handleWebsocketError = useHandleWebsocketError();

  const [customAvatarUrl, setCustomAvatarUrl] = useState<string | null>(null);
  const [currentAvatarId, setCurrentAvatarId] = useState<string | null>(null);
  const [userAvatars, setUserAvatars] = useState<IUserAvatar[] | []>([]);
  const [unfinishedAvatar, setUnfinishedAvatar] = useState<{
    avatarId: string;
    avaturnId: string;
  } | null>(null);
  const [wantsToCreateAvatar, setWantsToCreateAvatar] =
    useState<boolean>(false);
  const [isCreatingGenericAvaturn, setIsCreatingGenericAvaturn] =
    useState<boolean>(false);
  const [avaturnToProcess, setAvaturnToProcess] =
    useState<INewAvaturnData | null>(null);
  const [configs, setConfigs] = useState<IConfigs | null>(null);

  const [loginError, setLoginError] = useState<string | null>(null);

  const [currentScreen, setCurrentScreen] = useState<CurrentScreenEnum>(
    CurrentScreenEnum.DEFAULT,
  );

  const [skipLinkInHeader, setSkipLinkInHeader] = useState<(() => void) | null>(
    null,
  );

  const prepareWebSocketUrl = (): string => {
    const webSocketUrl = new URL(env.REACT_APP_SPATIAL_MANAGER_WEB_SOCKET_URL);
    webSocketUrl.searchParams.append('token', token.current);
    webSocketUrl.searchParams.append('session', websocketSessionIdRef.current);

    return webSocketUrl.toString();
  };

  const isConnect = () => {
    if (window.location.href.includes(ROUTES.CUSTOM_AVATURN)) {
      return false;
    }
    return true;
  };

  const getWebsocketUrl = useCallback(
    (): Promise<string> =>
      new Promise<string>((resolve) => {
        if (token.current) {
          resolve(prepareWebSocketUrl());
        } else {
          const interval = setInterval(() => {
            if (token.current) {
              clearInterval(interval);
              resolve(prepareWebSocketUrl());
            }
          }, 1000);
        }
      }),
    [],
  );

  const getWebSocketOptions: GetWebSocketOptionsType = useCallback(
    ({ withReconnection, disableJson = false, ...restOptions }) => {
      let options: Options = {
        share: true,
        disableJson,
        ...restOptions,
        onClose: (event) => {
          if (withReconnection) {
            mytaverseWebsocketLogger.log(
              'Disconnected from mytaverse websocket',
            );
            handleWebsocketError(event);
          }
        },
      };

      if (withReconnection) {
        options = {
          ...options,
          retryOnError: true,
          reconnectAttempts: 60,
          reconnectInterval: 1000,
          shouldReconnect: () => {
            mytaverseWebsocketLogger.log('Reconnecting to websocket');
            return true;
          },
          onOpen: () => {
            mytaverseWebsocketLogger.log('Connected to mytaverse websocket');
          },
          onError: (event) => {
            mytaverseWebsocketLogger.error(
              `Mytaverse websocket error: ${JSON.stringify(event)}`,
            );
            reloadPageTimeout(10, 'Mytaverse websocket error');
          },
        };
      }

      return options;
    },
    [],
  );

  const { sendJsonMessage } = useWebSocket(
    getWebsocketUrl,
    getWebSocketOptions({
      withReconnection: true,
      disableJson: true,
      filter: () => {
        return false;
      },
    }),
    isConnect(),
  );

  const [websocketConnectionInfo, setWebsocketConnectionInfo] =
    useState<IWebSocketConnectionInfo>();

  const sendMessageToUnreal = useCallback(
    (message: any) => {
      sendJsonMessage({
        action: 'MESSAGE_TO_UNREAL',
        message,
      });
    },
    [sendJsonMessage],
  );

  const sendMessageToEvent = useCallback(
    (eventId: string, message: any) => {
      sendJsonMessage({
        action: 'MESSAGE_TO_EVENT',
        eventId,
        message,
      });
    },
    [sendJsonMessage],
  );

  const sendMessageToRoom = useCallback(
    (roomId: string, participantId: string, eventId: string, text: string) => {
      sendJsonMessage({
        action: 'MESSAGE_TO_ROOM',
        roomId,
        participantId,
        eventId,
        text,
      });
    },
    [sendJsonMessage],
  );

  const sendMessageToParticipant = useCallback(
    (participantId: string, message: any) => {
      sendJsonMessage({
        action: 'MESSAGE_TO_PARTICIPANT',
        participantId,
        message,
      });
    },
    [sendJsonMessage],
  );

  const changeUserProfile = async (data: InitialValues) => {
    if (user) {
      setChangingUserData(true);
      let updUser;

      try {
        updUser = await UserService.changeUser(user.id || user.participantId, {
          name: data.username,
          company: data.company,
          linkedIn: data.linkedIn,
          phoneNumber: data.phoneNumber,
          profileImage: data.profileImage,
        });

        setUser({ ...user, ...updUser });
      } catch (e) {
        setChangingUserData(false);
      }

      setChangingUserData(false);
    }
  };

  useAsyncEffect(async () => {
    Sentry.getCurrentScope().setTag('sessionId', websocketSessionIdRef.current);

    MytaverseLogger.log(`SessionId: ${websocketSessionIdRef.current}`);

    const {
      data: { version },
    } = await axios.get(`${REACT_APP_API_URL}api/version`);

    MytaverseLogger.log(`Hub version v${REACT_APP_HUB_VERSION}`);
    MytaverseLogger.log(`API version: v${version}`);

    setSentryApiVersion(version);
    setSentryFrontendVersion(REACT_APP_HUB_VERSION);
  }, [websocketSessionIdRef.current]);

  useEffect(() => {
    localStorage.setItem('language', selectedLanguage);
    i18n.changeLanguage(
      selectedLanguage ? selectedLanguage.toLowerCase() : 'en',
    );
  }, [selectedLanguage]);

  // useEffect(() => {
  //   if (
  //     pathname.startsWith(ROUTES.LOGIN) &&
  //     localStorage.getItem('userId') &&
  //     !pathname.includes(ROUTES.CONFIRM_EVENT) &&
  //     !pathname.includes(ROUTES.REGISTER) &&
  //     !pathname.includes('confirm')
  //   ) {
  //     sessionStorage.getItem('selectedEventId')
  //       ? navigate(ROUTES.HOME_PAGE)
  //       : navigate(ROUTES.SELECT_EVENT);
  //   }
  // }, [pathname, user]);

  useEffect(() => {
    if (!isAuthenticated || isAuthLoading || loading) return;

    if (pathname === ROUTES.HOME_PAGE || pathname.startsWith(ROUTES.LOGIN)) {
      const previousPage = [...history]
        .reverse()
        .find((p) => p !== ROUTES.HOME_PAGE && !p.startsWith(ROUTES.LOGIN));

      if (previousPage) {
        navigate(previousPage, { replace: true });
      } else {
        const selectedEventId = sessionStorage.getItem('selectedEventId');
        navigate(selectedEventId ? ROUTES.HOME_PAGE : ROUTES.SELECT_EVENT, {
          replace: true,
        });
      }
    }
  }, [pathname, isAuthenticated, isAuthLoading, loading]);

  const clearUserLocalStorage = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    sessionStorage.removeItem('selectedEventId');
    localStorage.removeItem('currentRoomId');
    Cookies.remove('tutorial');
    Cookies.remove('token');
  };

  const logoutUser = React.useCallback(async () => {
    logoutClicked.current = true;
    setLoading(true);
    setUserId(null);
    setUser(null);
    setCurrentAvatarId(null);
    setCustomAvatarUrl(null);

    token.current = '';
    Cookies.remove('token');
    removeLocalStorageValue(StorageValues.DismissMessages);

    clearUserLocalStorage();
    hideNotification();
    signOut();
    // there will be a redirection to / (then /login) after that, triggering a refetch of the
    // bundle and clearing all the flags (loading to false, isAuthenticated to false...)
  }, [
    setLoading,
    setUserId,
    setUser,
    setCurrentAvatarId,
    setCustomAvatarUrl,
    removeLocalStorageValue,
    clearUserLocalStorage,
    hideNotification,
    signOut,
  ]);

  useHandleApiErrors(logoutUser);

  useEffect(() => {
    const websocketHealthCheckInterval = setInterval(() => {
      sendJsonMessage({ action: 'HEALTH_CHECK' });
    }, 30000);

    return () => {
      if (websocketHealthCheckInterval) {
        clearInterval(websocketHealthCheckInterval);
      }
    };
  }, [sendJsonMessage]);

  useEffect(() => {
    const isDebug = JSON.parse(localStorage.getItem('isDebugMode') || 'false');
    setIsDebugMode(isDebug);
  }, []);

  useEffect(() => {
    document.addEventListener(
      'click',
      () => {
        setPageClicked(true);
        MytaverseLogger.log('Page clicked');
      },
      { once: true },
    );
  }, []);

  useAsyncEffect(async () => {
    let refreshAuthTokensInterval: NodeJS.Timer | null = null;
    const sessionTokens = await getAuthTokens();

    if (sessionTokens) {
      const exp = sessionTokens?.accessToken?.payload?.exp;

      if (exp) {
        refreshAuthToken(exp);
      }

      axios.interceptors.request.use(async (config: AxiosRequestConfig) => {
        if (!token.current) {
          await fetchAndSetAuthTokens();
        }

        if (token.current !== null) {
          config.headers = {
            ...(config.headers || {}),
            Authorization: `Bearer ${token.current}`,
            // 'Id-Token': idToken.current,
          };
        }

        return config;
      });

      setIsAuthenticated(true);

      refreshAuthTokensInterval = setInterval(async () => {
        MytaverseLogger.log('Refresh auth tokens');
        await fetchAndSetAuthTokens();
      }, 600000);
    }

    setIsAuthLoading(false);

    return () => {
      if (refreshAuthTokensInterval) {
        clearInterval(refreshAuthTokensInterval);
      }
    };
  }, []);

  useEffect(() => {
    if (currentScreen) {
      sendJsonMessage({
        action: WebsocketAction.EventStage,
        stage: currentScreen,
      });
    }
  }, [currentScreen, sendJsonMessage]);

  useAsyncEffect(async () => {
    if (isAuthLoading) {
      return;
    }

    setLoading(true);

    const theme = await getAppTheme();
    const appSettings = await getAppSettings();
    setAppTheme(theme);
    setNotificationTheme(theme);

    if (appSettings) {
      setAppSettings(appSettings);
    }

    // if not logged user got to private route
    if (!isAuthenticated) {
      if (pathname.includes(ROUTES.LOGOUT) && searchParams.has('reason')) {
        if (searchParams.get('reason') === 'AUTO_MERGE_ACCOUNT') {
          enqueueSnackbar(
            'We merged your account to existing. Try to login again',
            { variant: 'success' },
          );
        }
      }

      if (
        pathname.includes(ROUTES.CUSTOM_AVATURN) &&
        searchParams.get('reason') === 'CUSTOM_AVATURN_FROM_QR' &&
        searchParams.has('token')
      ) {
        setLoading(false);
        return;
      }

      if (!pathname.includes(ROUTES.LOGIN)) {
        setLoading(false);
        return navigate(ROUTES.LOGIN);
      }
    } else {
      try {
        setLoading(true);
        const configsData = await ParticipantsService.getConfigs();
        setConfigs(configsData);

        let currentUserForNav = user;

        if (!user && isAuthenticated) {
          const cognitoUser = await authUser();
          const fetchedUser =
            await ParticipantsService.getCurrentParticipantProfile();
          const allAvatars =
            await ParticipantsService.getParticipantAvaturnAvatars();

          const unfinishedAvatar = allAvatars.find(
            ({ status, hasPhotoUrls, hasAvatarUrl }) =>
              [AvaturnStatuses.WH_AVATAR_READY].includes(status) &&
              !hasAvatarUrl &&
              hasPhotoUrls,
          );
          if (unfinishedAvatar) {
            setUnfinishedAvatar({
              avatarId: unfinishedAvatar.avatarId,
              avaturnId: unfinishedAvatar.avaturnId,
            });
          }

          setUserId(fetchedUser.id);
          setUser({
            ...fetchedUser,
            isFirstTimeUser: !(
              fetchedUser?.state?.lastEntranceTimestamp &&
              Object.keys(fetchedUser?.state?.lastEntranceTimestamp).length > 0
            ),
          });
          setUserAvatars(fetchedUser?.avatars);
          setSentryAuthUserData({
            id: cognitoUser.username as string,
            email: '',
            firstName: '',
            lastName: '',
          });

          fetchedUser?.avatars?.forEach((avatar) => {
            const img = new Image();
            if (avatar?.avatarPic) {
              img.src = avatar.avatarPic; // Triggers preload
            }

            if (
              fetchedUser?.activeAvatarId &&
              avatar.avatarId === fetchedUser.activeAvatarId
            ) {
              setCurrentAvatarId(fetchedUser.activeAvatarId);
              setCustomAvatarUrl(avatar?.avatarUrl || '');
            }
          });

          currentUserForNav = fetchedUser;
        }

        const isProfileComplete =
          currentUserForNav?.name?.trim() &&
          currentUserForNav?.gender &&
          ['male', 'female'].includes(currentUserForNav.gender);

        if (pathname === ROUTES.HOME_PAGE || pathname.includes(ROUTES.LOGIN)) {
          if (isProfileComplete) {
            navigate(ROUTES.SELECT_EVENT);
          }
        }

        setLoading(false);
      } catch (error: unknown | Error) {
        setLoading(false);
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          // @ts-ignore
          const messageCodeFromError = error.response?.data?.message;
          let messageCode = 'accessDeniedError';
          if (
            messageCodeFromError &&
            i18n.exists(`login.${messageCodeFromError}`, {
              ns: [LANGUAGE_NAMESPACE],
            })
          ) {
            messageCode = messageCodeFromError;
          }

          setIsLogoutForced({
            reason: LogoutReasonEnum.UNAUTHORISED,
            messageCode: messageCode,
          });
        } else {
          setIsLogoutForced({
            reason: LogoutReasonEnum.SESSION_EXPIRED,
            messageCode: 'loginAgain',
          });
        }
      }
    }
  }, [isAuthenticated, isAuthLoading, authUser]);

  const selectLanguageHandler = React.useCallback(
    (language: string) => {
      setSelectedLanguage(language);
      localStorage.setItem('language', language);
      i18n.changeLanguage(language ? language.toLowerCase() : 'en');
    },
    [setSelectedLanguage],
  );

  const navigateToEventsPage = ({
    title = '',
    message = '',
    isModal = false,
    type = NOTIFICATION_TYPES.WARNING,
    translateMessage = true,
  } = {}) => {
    navigate(ROUTES.SELECT_EVENT);

    if (!message) {
      return;
    }

    if (isModal) {
      setIsLogoutForced({
        reason: LogoutReasonEnum.SUPPORT,
        messageCode: message,
      });
    } else {
      showNotification(
        getNotification({
          title: translateMessage ? translate(title) : title,
          message: translateMessage ? translate(message) : message,
          type,
        }),
      );
    }
  };

  const trackAnalytics = React.useCallback(
    async (type: string, payload: any = {}) => {
      const websocketSessionId = websocketSessionIdRef.current;

      if (websocketSessionId && userId) {
        const { eventId } = payload;

        if (eventId) {
          delete payload.eventId;
        }

        return AnalyticsService.track(type, Date.now(), {
          ...(eventId ? { eventId } : {}),
          participantId: userId,
          sessionId: websocketSessionId,
          payload,
        });
      }
    },
    [userId, websocketSessionIdRef],
  );

  useEffect(() => {
    Hub.listen('auth', (data) => {
      const { payload } = data;

      if (payload.event == 'signInWithRedirect') {
        trackLoginEvent.current = true;
      }
    });
  }, []);

  useEffect(() => {
    if (
      trackLoginEvent.current &&
      token.current &&
      websocketSessionIdRef.current &&
      user
    ) {
      trackAnalytics(AnalyticsType.Login, {
        device: [
          [browserName, browserVersion].filter(Boolean).join(' '),
          [osName, osVersion].filter(Boolean).join(' '),
        ].join(', '),
        deviceType: getDeviceProfile(),
        timezone: format(new Date(), 'xxx'),
      });
      trackLoginEvent.current = false;
    }
  }, [
    trackLoginEvent.current,
    token.current,
    websocketSessionIdRef.current,
    user,
    trackAnalytics,
  ]);

  const isLogoutClicked = () => logoutClicked.current;

  return (
    <MytaverseContext.Provider
      value={{
        token: token.current,
        // idToken: idToken.current,
        configs,
        userId,
        user,
        userPermissions,
        loading: loading || isAuthLoading,
        isLogoutForced,
        setIsAuthenticated,
        setUser,
        setUserPermissions,
        setCurrentAvatarId,
        currentAvatarId,
        setCustomAvatarUrl,
        customAvatarUrl,
        setUserAvatars,
        userAvatars,
        unfinishedAvatar,
        setUnfinishedAvatar,
        changeUserProfile,
        changingUserData,
        setLoading,
        appTheme,
        appSettings,
        selectedLanguage,
        selectLanguage: selectLanguageHandler,
        loginError,
        setLoginError,

        signIn,
        logoutUser,

        websocketSessionId: websocketSessionIdRef.current,
        websocketConnectionInfo,
        setWebsocketConnectionInfo,
        getWebsocketUrl,
        getWebSocketOptions,

        sendMessageToUnreal,
        sendMessageToEvent,
        sendMessageToRoom,
        sendMessageToParticipant,
        isDebugMode,
        setIsDebugMode,
        sendJSONMessageToWebSocket: sendJsonMessage,

        navigateToEventsPage,

        trackAnalytics,
        isLogoutClicked,
        currentScreen,
        setCurrentScreen,
        wantsToCreateAvatar,
        setWantsToCreateAvatar,
        isCreatingGenericAvaturn,
        setIsCreatingGenericAvaturn,
        avaturnToProcess,
        setAvaturnToProcess,
        pageClicked,
        setPageClicked,
        showFeedbackForm,
        setShowFeedbackForm,
        skipLinkInHeader,
        setSkipLinkInHeader,
      }}
    >
      {children}
    </MytaverseContext.Provider>
  );
};
